import React, { Fragment, useCallback, useEffect, useContext } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { VIEW_PAGE_RESTRICTIONS_BUTTON_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { getMark } from '@confluence/performance/entry-points/performance.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
// TODO(brizvash): allow color and size changes for icons
import { useContentId } from '@confluence/page-restrictions-context/entry-points/useContentId.ts';
import { usePageRestrictionsContext } from '@confluence/page-restrictions-context/entry-points/PageRestrictionsContext.ts';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator.ts';

import { RESTRICTIONS_BUTTON_METRIC } from '../perf.config.ts';
import { RestrictionsDataContainer } from '../RestrictionsDialog/RestrictionsDataContainer.tsx';

import { RestrictionsButtonPlaceholder } from './RestrictionsButtonPlaceholder.tsx';
import { RestrictionButtonComponent } from './RestrictionsButtonComponent.tsx';

const restrictionButtonContainerStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		font: token('font.body'),
	},
});

const hiddenStyles = css({ display: 'none' });

export const RestrictionsButton = (props: RestrictionsButtonProps) => {
	const {
		className,
		onClick,
		isDisabled,
		resetDialogState,
		restrictionsButtonSSRRendered = false,
		restrictionsButtonSSRLocation = '',
		triggeredFrom = '',
	} = props;
	const contentId = useContentId(props.contentId);

	const { isExternalCollaborator: isGuest } = useIsExternalCollaborator();

	const isRestrictionsButtonSSRRendered: boolean = Boolean(
		window.__SSR_RENDERED__ && restrictionsButtonSSRRendered,
	);

	const restrictionsButtonClicked = useCallback(
		(
			restrictionsDataContainer: RestrictionsDataContainer,
			hasRestrictions: boolean,
			hasInheritedRestrictions: boolean,
			hasDirectViewRestrictions: boolean,
		) => {
			restrictionsDataContainer.setData({
				hasRestrictions,
				hasInheritedRestrictions,
				hasDirectViewRestrictions,
			});

			onClick();
		},
		[onClick],
	);

	const { transitionId } = useContext(RoutesContext);

	useEffect(() => {
		if (resetDialogState && transitionId) {
			resetDialogState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [transitionId]);

	useEffect(() => {
		if (isRestrictionsButtonSSRRendered) {
			//set FMP now if SSR rendered for default restrictions dialog
			RESTRICTIONS_BUTTON_METRIC.markFMP(getMark('CFP-63.ssr-ttr'));
		}
	}, [isRestrictionsButtonSSRRendered]);

	const restrictionButtonGenerator = useCallback(
		(
			restrictionsDataContainer: RestrictionsDataContainer,
			hasDirectViewRestrictions: boolean,
			hasInheritedViewRestrictions: boolean,
			hasRestrictions: boolean,
		) => {
			return (
				<Fragment>
					<span
						css={restrictionButtonContainerStyle}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={className || ''}
						data-testid="restriction-button"
					>
						<RestrictionButtonComponent
							hasDirectViewRestrictions={hasDirectViewRestrictions}
							hasInheritedViewRestrictions={hasInheritedViewRestrictions}
							hasRestrictions={hasRestrictions}
							onClick={() =>
								restrictionsButtonClicked(
									restrictionsDataContainer,
									hasDirectViewRestrictions,
									hasInheritedViewRestrictions,
									hasRestrictions,
								)
							}
							isDisabled={isDisabled}
						/>
					</span>
					<ExperienceSuccess name={VIEW_PAGE_RESTRICTIONS_BUTTON_EXPERIENCE} />
					<PageSegmentLoadEnd
						key={`end-${contentId}`}
						metric={RESTRICTIONS_BUTTON_METRIC}
						customData={{
							restrictionsButtonSSRRendered,
							restrictionsButtonSSRLocation,
							triggeredFrom,
						}}
					/>
				</Fragment>
			);
		},
		[
			className,
			restrictionsButtonClicked,
			isDisabled,
			contentId,
			restrictionsButtonSSRRendered,
			restrictionsButtonSSRLocation,
			triggeredFrom,
		],
	);

	const { restrictions, loading } = usePageRestrictionsContext();

	const isQueryLoading = !restrictions && loading;

	if (!contentId) return null;
	return (
		<Subscribe to={[RestrictionsDataContainer]}>
			{(restrictionsDataContainer: RestrictionsDataContainer) => {
				if (isQueryLoading || isGuest) {
					return (
						// We are using this container as a hacky way to hide the button when the user is a Guest (External
						// Collaborator).  Normally we would just not render the component if we wanted to hide it, but in
						// Fabric editor this button is rendered by a function passed as a prop to the AkEditor control,
						// and the AkEditor will throw an error if the function doesn't render anything. So instead we will
						// render the placeholder but hide it via CSS if the user is a Guest.  This makes the AkEditor
						// happy while not showing the button.
						<div css={isGuest && hiddenStyles} data-test-id="restriction-placeholder-container">
							<RestrictionsButtonPlaceholder />
						</div>
					);
				}

				const hasDirectViewRestrictions = Boolean(restrictions?.hasViewRestrictions);
				const hasInheritedRestrictions = Boolean(restrictions?.hasInheritedRestrictions);
				const hasRestrictions = Boolean(restrictions?.hasRestrictions);
				return restrictionButtonGenerator(
					restrictionsDataContainer,
					hasDirectViewRestrictions,
					hasInheritedRestrictions,
					hasRestrictions,
				);
			}}
		</Subscribe>
	);
};

type RestrictionsButtonProps = {
	className?: string;
	contentId?: string;
	onClick(): void;
	resetDialogState?(): void;
	isDisabled?: boolean;
	restrictionsButtonSSRRendered?: boolean;
	restrictionsButtonSSRLocation?: string;
	triggeredFrom?: string;
};
